import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSeriesDetail } from '../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Play, Star, Calendar, Clock, User, Film, ChevronDown } from 'lucide-react';

export default function SeriesDetailPage() {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSeason, setActiveSeason] = useState(null);
  const [seasonOpen, setSeasonOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    getSeriesDetail(seriesId)
      .then((data) => {
        setDetail(data);
        const firstSeason = Object.keys(data.episodes || {})[0];
        setActiveSeason(firstSeason || null);
      })
      .catch(() => {
        toast.error('Could not load series details');
        navigate('/series');
      })
      .finally(() => setLoading(false));
  }, [seriesId, navigate]);

  if (loading) {
    return (
      <div className="series-detail-loading" data-testid="series-detail-loading">
        <div className="loading-spinner" />
        <span>Loading series...</span>
      </div>
    );
  }

  if (!detail) return null;

  const { info, seasons, episodes } = detail;
  const seasonKeys = Object.keys(episodes || {}).sort((a, b) => parseInt(a) - parseInt(b));
  const currentEpisodes = activeSeason ? (episodes[activeSeason] || []) : [];

  const activeSeasonLabel = () => {
    if (!activeSeason) return 'Select Season';
    const s = seasons?.find(s => String(s.season_number) === String(activeSeason));
    return s ? s.name : `Season ${activeSeason}`;
  };

  const playEpisode = (ep) => {
    if (!ep.stream_url) { toast.error('Stream URL not available'); return; }
    navigate(`/watch/ep_${ep.id}`, {
      state: {
        streamUrl:       ep.stream_url,         // HLS (.m3u8) — try first
        streamUrlTs:     ep.stream_url_ts,      // MPEG-TS (.ts) — fallback
        streamUrlNative: ep.stream_url_native,  // native container — last resort
        title: `${info.name} — ${ep.title}`,
        containerExtension: ep.container_extension,
        isEpisode: true,
      }
    });
  };

  const backdrop = info.backdrop_path?.[0] || info.cover || '';
  const poster = info.cover || '';

  return (
    <div className="series-detail-page" data-testid="series-detail-page">
      {/* Hero Section */}
      <div
        className="series-hero"
        style={{ backgroundImage: backdrop ? `url(${backdrop})` : 'none' }}
        data-testid="series-hero"
      >
        <div className="series-hero-overlay" />
        <div className="series-hero-content">
          <button className="series-back-btn" onClick={() => navigate('/series')} data-testid="series-back-btn">
            <ArrowLeft className="w-5 h-5" />
            Back to Series
          </button>

          <div className="series-hero-body">
            {poster && (
              <div className="series-poster" data-testid="series-poster">
                <img src={poster} alt={info.name} />
              </div>
            )}
            <div className="series-meta">
              <h1 className="series-title" data-testid="series-title">{info.name}</h1>

              <div className="series-badges">
                {info.rating && (
                  <span className="badge badge-rating">
                    <Star className="w-3 h-3" /> {parseFloat(info.rating).toFixed(1)}
                  </span>
                )}
                {info.releaseDate && (
                  <span className="badge">
                    <Calendar className="w-3 h-3" /> {info.releaseDate?.substring(0, 4)}
                  </span>
                )}
                {info.episode_run_time && (
                  <span className="badge">
                    <Clock className="w-3 h-3" /> {info.episode_run_time} min
                  </span>
                )}
                {info.genre && <span className="badge">{info.genre}</span>}
              </div>

              {info.plot && (
                <p className="series-plot" data-testid="series-plot">{info.plot}</p>
              )}

              <div className="series-credits">
                {info.director && (
                  <div className="credit-item">
                    <Film className="w-4 h-4 text-[rgb(124,58,237)]" />
                    <span className="credit-label">Director</span>
                    <span className="credit-value">{info.director}</span>
                  </div>
                )}
                {info.cast && (
                  <div className="credit-item">
                    <User className="w-4 h-4 text-[rgb(124,58,237)]" />
                    <span className="credit-label">Cast</span>
                    <span className="credit-value">{info.cast}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Episodes Section */}
      <div className="series-episodes-section" data-testid="series-episodes-section">
        <div className="series-episodes-header">
          <h2 className="episodes-heading">Episodes</h2>

          {/* Season Dropdown */}
          {seasonKeys.length > 0 && (
            <div className="season-selector" data-testid="season-selector">
              <button
                className="season-dropdown-btn"
                onClick={() => setSeasonOpen(!seasonOpen)}
                data-testid="season-dropdown-toggle"
              >
                {activeSeasonLabel()}
                <ChevronDown className={`w-4 h-4 transition-transform ${seasonOpen ? 'rotate-180' : ''}`} />
              </button>
              {seasonOpen && (
                <div className="season-dropdown-menu" data-testid="season-dropdown-menu">
                  {seasonKeys.map((sk) => {
                    const s = seasons?.find(s => String(s.season_number) === String(sk));
                    const label = s ? s.name : `Season ${sk}`;
                    return (
                      <button
                        key={sk}
                        className={`season-option ${activeSeason === sk ? 'active' : ''}`}
                        onClick={() => { setActiveSeason(sk); setSeasonOpen(false); }}
                        data-testid={`season-option-${sk}`}
                      >
                        {label}
                        <span className="season-ep-count">{(episodes[sk] || []).length} eps</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Episode List */}
        <div className="episode-list" data-testid="episode-list">
          {currentEpisodes.length === 0 ? (
            <p className="text-white/40 text-center py-10">No episodes available</p>
          ) : (
            currentEpisodes.map((ep) => (
              <div key={ep.id} className="episode-card" data-testid={`episode-${ep.id}`}>
                <div className="episode-thumb">
                  {ep.cover ? (
                    <img src={ep.cover} alt={ep.title} loading="lazy" />
                  ) : (
                    <div className="episode-thumb-placeholder">
                      <Play className="w-6 h-6 text-white/30" />
                    </div>
                  )}
                </div>
                <div className="episode-info">
                  <div className="episode-num">E{ep.episode_num}</div>
                  <h4 className="episode-title">{ep.title}</h4>
                  {ep.plot && <p className="episode-plot">{ep.plot}</p>}
                  {ep.duration && (
                    <span className="episode-duration">
                      <Clock className="w-3 h-3" /> {ep.duration}
                    </span>
                  )}
                </div>
                <button
                  className="episode-play-btn"
                  onClick={() => playEpisode(ep)}
                  data-testid={`play-episode-${ep.id}`}
                >
                  <Play className="w-5 h-5" />
                  Play
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
